import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Users, Kanban, List, Activity, Search, Plus, Loader2,
  Mail, CheckSquare, Trophy, Zap, DollarSign, Sun, FileText, Upload, PenSquare,
} from 'lucide-react';
import PipelineKanban from './PipelineKanban';
import DealProfile from './DealProfile';
import TeamManagement from './TeamManagement';
import EmailIntegration from './EmailIntegration';
import TasksPanel from './TasksPanel';
import RepPerformance from './RepPerformance';
import EmailSequences from './EmailSequences';
import { CrmSidebar } from './components/CrmSidebar';
import { StatsCards } from './components/StatsCards';
import { PipelineMiniBar } from './components/PipelineMiniBar';
import { DealsListView } from './components/DealsListView';
import { NewDealModal } from './components/NewDealModal';
import { ActivityFeed } from './components/ActivityFeed';
import { RatesManagement } from './components/RatesManagement';
import { CsvImportModal } from './components/CsvImportModal';
import { ActiveContracts } from './components/ActiveContracts';
import { CommissionDashboard } from './components/CommissionDashboard';
import { MyDay } from './components/MyDay';
import { BlogManager } from './components/BlogManager';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [section, setSection] = useState('my_day');
  const [viewMode, setViewMode] = useState('kanban'); // kanban or list
  const [deals, setDeals] = useState([]);
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState({ pipeline: {}, total_deals: 0, total_value: 0, won_value: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNewDeal, setShowNewDeal] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [activities, setActivities] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [staleCount, setStaleCount] = useState(0);
  const [userRole, setUserRole] = useState('sales_rep');
  const [myPermissions, setMyPermissions] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [dealsRes, statsRes, activitiesRes, tasksRes, staleRes, permsRes] = await Promise.all([
        axios.get(`${API}/api/admin/deals`, { withCredentials: true }),
        axios.get(`${API}/api/admin/pipeline/stats`, { withCredentials: true }),
        axios.get(`${API}/api/admin/activities`, { withCredentials: true }).catch(() => ({ data: { activities: [] } })),
        axios.get(`${API}/api/admin/my-tasks`, { withCredentials: true }).catch(() => ({ data: { tasks: [] } })),
        axios.get(`${API}/api/admin/deals/stale`, { withCredentials: true }).catch(() => ({ data: { stale_deals: [] } })),
        axios.get(`${API}/api/admin/permissions/me`, { withCredentials: true }).catch(() => ({ data: { permissions: {} } })),
      ]);
      setDeals(dealsRes.data.deals || []);
      setTeam(dealsRes.data.team || []);
      setStats(statsRes.data);
      setUserRole(dealsRes.data.user_role || statsRes.data.user_role || 'sales_rep');
      setActivities(activitiesRes.data.activities || []);
      setMyTasks(tasksRes.data.tasks || []);
      setStaleCount(staleRes.data.stale_deals?.length || 0);
      setMyPermissions(permsRes.data.permissions || {});
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredDeals = search
    ? deals.filter(item => Object.values(item).some(v => String(v).toLowerCase().includes(search.toLowerCase())))
    : deals;

  const overdueCount = myTasks.filter(t => t.is_overdue).length;

  const sideNav = [
    { id: 'my_day', label: 'My Day', icon: <Sun size={18} /> },
    { id: 'pipeline', label: 'Pipeline', icon: <Kanban size={18} />, count: stats.total_deals, alert: staleCount > 0 },
    { id: 'contracts', label: 'Active Contracts', icon: <FileText size={18} /> },
    { id: 'tasks', label: 'Tasks', icon: <CheckSquare size={18} />, count: overdueCount || undefined, isOverdue: overdueCount > 0 },
    { id: 'commission', label: 'My Commission', icon: <DollarSign size={18} /> },
    { id: 'sequences', label: 'Sequences', icon: <Zap size={18} /> },
    { id: 'email', label: 'Email', icon: <Mail size={18} /> },
    { id: 'performance', label: 'Performance', icon: <Trophy size={18} /> },
    { id: 'rates', label: 'Rates', icon: <DollarSign size={18} /> },
    { id: 'team', label: 'Team', icon: <Users size={18} /> },
    { id: 'blog', label: 'Blog', icon: <PenSquare size={18} /> },
    { id: 'activity', label: 'Activity', icon: <Activity size={18} /> },
  ];

  // Filter sidebar by permissions where relevant
  const visibleSideNav = sideNav.filter(item => {
    if (item.id === 'contracts' && !myPermissions.view_all_contracts && userRole !== 'admin') {
      // still show; will be scoped to own deals server-side
    }
    if (item.id === 'commission' && !myPermissions.view_commissions && userRole !== 'admin') return false;
    if (item.id === 'rates' && !myPermissions.manage_rates && userRole !== 'admin') return false;
    if (item.id === 'team' && !myPermissions.manage_team && userRole !== 'admin') return false;
    if (item.id === 'blog' && !['admin', 'manager'].includes(userRole)) return false;
    return true;
  });

  const handleBellNavigate = (dealId) => {
    const deal = deals.find(d => d.id === dealId);
    if (deal) {
      setSelectedDeal(deal);
      setSection('pipeline');
    }
  };

  const sidebar = (
    <CrmSidebar
      navItems={visibleSideNav}
      section={section}
      onSelect={(id) => { setSection(id); setSelectedDeal(null); }}
      userLabel={user?.name || user?.email}
      onLogout={logout}
      onBellNavigate={handleBellNavigate}
    />
  );

  if (loading) {
    return (
      <main className="page-content" style={{ display: 'flex' }}>
        {sidebar}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
        </div>
      </main>
    );
  }

  return (
    <main className="page-content" data-testid="admin-dashboard" style={{ display: 'flex' }}>
      {sidebar}
      <div style={{ flex: 1, background: '#F9FAFB', minHeight: '100vh', overflow: 'auto' }}>
        <div style={{ padding: '1.25rem 1.5rem' }}>
          {/* ===== MY DAY SECTION ===== */}
          {section === 'my_day' && (
            <MyDay
              onSelectDeal={(id) => {
                const deal = deals.find(d => d.id === id);
                if (deal) { setSelectedDeal(deal); setSection('pipeline'); }
              }}
              onOpenNewDeal={() => setShowNewDeal(true)}
            />
          )}

          {/* ===== ACTIVE CONTRACTS SECTION ===== */}
          {section === 'contracts' && <ActiveContracts />}

          {/* ===== COMMISSION SECTION ===== */}
          {section === 'commission' && <CommissionDashboard />}

          {/* ===== PIPELINE SECTION ===== */}
          {section === 'pipeline' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
                    Sales Pipeline
                  </h2>
                  <p style={{ fontSize: '0.813rem', color: '#9CA3AF', margin: 0 }}>{deals.length} total deals</p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  {/* View Toggle */}
                  <div style={{ display: 'flex', background: '#E5E7EB', borderRadius: 6, padding: 2 }}>
                    <button
                      onClick={() => setViewMode('kanban')}
                      data-testid="view-kanban-btn"
                      style={{
                        padding: '0.375rem 0.75rem', borderRadius: 4, border: 'none', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: 500,
                        background: viewMode === 'kanban' ? '#fff' : 'transparent',
                        color: viewMode === 'kanban' ? 'var(--navy)' : '#6B7280',
                      }}
                    >
                      <Kanban size={14} style={{ marginRight: 4, display: 'inline', verticalAlign: 'middle' }} />
                      Board
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      data-testid="view-list-btn"
                      style={{
                        padding: '0.375rem 0.75rem', borderRadius: 4, border: 'none', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: 500,
                        background: viewMode === 'list' ? '#fff' : 'transparent',
                        color: viewMode === 'list' ? 'var(--navy)' : '#6B7280',
                      }}
                    >
                      <List size={14} style={{ marginRight: 4, display: 'inline', verticalAlign: 'middle' }} />
                      List
                    </button>
                  </div>

                  {/* Search */}
                  <div style={{ position: 'relative', width: 240 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                    <Input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search deals..."
                      data-testid="admin-search"
                      style={{ paddingLeft: 32, background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)', fontSize: '0.813rem', height: 36 }}
                    />
                  </div>

                  {/* Import CSV */}
                  {(myPermissions.import_leads || userRole === 'admin') && (
                    <Button
                      onClick={() => setShowCsvImport(true)}
                      className="btn-secondary"
                      data-testid="import-csv-btn"
                    >
                      <Upload size={14} className="mr-1" /> Import CSV
                    </Button>
                  )}

                  {/* Add Deal */}
                  <Button onClick={() => setShowNewDeal(true)} className="btn-primary" data-testid="add-deal-btn">
                    <Plus size={16} className="mr-1" /> Add Deal
                  </Button>
                </div>
              </div>

              <StatsCards stats={stats} userRole={userRole} />
              <PipelineMiniBar pipeline={stats.pipeline} onStageClick={setSearch} />

              {viewMode === 'kanban' ? (
                <PipelineKanban
                  onDealClick={(deal) => setSelectedDeal(deal)}
                  onRefresh={fetchData}
                />
              ) : (
                <DealsListView deals={filteredDeals} userRole={userRole} onDealClick={setSelectedDeal} />
              )}
            </>
          )}

          {/* ===== TEAM SECTION ===== */}
          {section === 'team' && <TeamManagement />}

          {/* ===== BLOG SECTION ===== */}
          {section === 'blog' && <BlogManager />}

          {/* ===== TASKS SECTION ===== */}
          {section === 'tasks' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <CheckSquare size={22} style={{ color: 'var(--teal)' }} />
                    My Tasks
                  </h2>
                  <p style={{ fontSize: '0.813rem', color: '#9CA3AF', margin: 0 }}>
                    {myTasks.length} pending tasks
                    {overdueCount > 0 && (
                      <span style={{ color: '#DC2626', marginLeft: '0.5rem' }}>
                        ({overdueCount} overdue)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', padding: '1rem' }}>
                <TasksPanel onTaskChange={fetchData} />
              </div>
            </>
          )}

          {/* ===== PERFORMANCE SECTION ===== */}
          {section === 'performance' && <RepPerformance />}

          {/* ===== RATES SECTION ===== */}
          {section === 'rates' && <RatesManagement userRole={userRole} />}

          {/* ===== SEQUENCES SECTION ===== */}
          {section === 'sequences' && <EmailSequences />}

          {/* ===== EMAIL SECTION ===== */}
          {section === 'email' && (
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', minHeight: 500 }}>
              <EmailIntegration />
            </div>
          )}

          {/* ===== ACTIVITY SECTION ===== */}
          {section === 'activity' && (
            <>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', marginBottom: '1rem' }}>
                Recent Activity
              </h2>
              <ActivityFeed activities={activities} />
            </>
          )}
        </div>
      </div>

      {/* ===== DEAL PROFILE PANEL ===== */}
      {selectedDeal && (
        <DealProfile
          dealId={selectedDeal.id}
          onClose={() => setSelectedDeal(null)}
          onUpdate={fetchData}
          team={team}
          userRole={userRole}
        />
      )}

      {/* ===== NEW DEAL MODAL ===== */}
      {showNewDeal && (
        <NewDealModal onClose={() => setShowNewDeal(false)} onCreated={fetchData} />
      )}

      {/* ===== CSV IMPORT MODAL ===== */}
      {showCsvImport && (
        <CsvImportModal onClose={() => setShowCsvImport(false)} onImported={fetchData} />
      )}
    </main>
  );
};

export default AdminDashboard;
